import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Divider,
  Typography,
  Row,
  Col,
  Alert,
  Timeline,
  Statistic,
} from "antd";
import {
  ArrowLeftOutlined,
  BarcodeOutlined,
  ShopOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { inventoryService } from "../services/inventoryService";

const { Title, Text } = Typography;

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssetDetails();
  }, [id]);

  const fetchAssetDetails = async () => {
    try {
      setLoading(true);
      const response = await inventoryService.getAssetById(id);
      if (response.success) {
        setAsset(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch asset details", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!asset) {
    return (
      <Card>
        <Alert
          message="Asset Not Found"
          description="The asset you're looking for doesn't exist."
          type="error"
          showIcon
          action={
            <Button onClick={() => navigate("/inventory")}>
              Back to Inventory
            </Button>
          }
        />
      </Card>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "InStock":
        return "green";
      case "Assigned":
        return "blue";
      case "Faulty":
        return "red";
      case "Retired":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "InStock":
        return "In Stock";
      case "Assigned":
        return "Assigned";
      case "Faulty":
        return "Faulty";
      case "Retired":
        return "Retired";
      default:
        return status;
    }
  };

  // Build timeline based on asset lifecycle
  const timelineItems = [];

  // Purchase stage
  if (asset.vendor || asset.purchaseDate) {
    timelineItems.push({
      color: "green",
      children: (
        <div>
          <Text strong>Purchased</Text>
          <br />
          {asset.purchaseDate && (
            <Text type="secondary">
              {new Date(asset.purchaseDate).toLocaleDateString()}
            </Text>
          )}
          {asset.vendor && (
            <>
              <br />
              <Text type="secondary">Vendor: {asset.vendor.vendorName}</Text>
            </>
          )}
        </div>
      ),
    });
  }

  // Inventory stage
  timelineItems.push({
    color: "blue",
    children: (
      <div>
        <Text strong>In Inventory</Text>
        <br />
        <Text type="secondary">
          Added: {new Date(asset.createdAt).toLocaleDateString()}
        </Text>
        {asset.status === "InStock" && (
          <>
            <br />
            <Tag color="green">Currently In Stock</Tag>
          </>
        )}
      </div>
    ),
  });

  // Order Assignment stage
  if (asset.order) {
    timelineItems.push({
      color: "orange",
      children: (
        <div>
          <Text strong>Assigned to Order</Text>
          <br />
          <Text type="secondary">
            Order: {asset.order.contractReference || `#${asset.order.id}`}
          </Text>
          <br />
          <Text type="secondary">
            Date: {new Date(asset.order.orderDate).toLocaleDateString()}
          </Text>
          {asset.customer && (
            <>
              <br />
              <Text type="secondary">
                Customer: {asset.customer.customerName}
              </Text>
            </>
          )}
        </div>
      ),
    });
  }

  // Current status
  if (asset.status === "Assigned" && asset.customer) {
    timelineItems.push({
      color: "purple",
      children: (
        <div>
          <Text strong>Currently Assigned</Text>
          <br />
          <Text type="secondary">Customer: {asset.customer.customerName}</Text>
          {asset.customer.customerMobile && (
            <>
              <br />
              <Text type="secondary">
                Phone: {asset.customer.customerMobile}
              </Text>
            </>
          )}
        </div>
      ),
    });
  }

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate("/inventory")}
                >
                  Back
                </Button>
                <div>
                  <Title level={3} style={{ margin: 0 }}>
                    <Space>
                      <BarcodeOutlined />
                      <span>Asset: {asset.serialNumber}</span>
                    </Space>
                  </Title>
                  <Text type="secondary">
                    {asset.item?.itemName} ({asset.item?.itemType})
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Space>
                <Tag
                  color={getStatusColor(asset.status)}
                  style={{ fontSize: "14px", padding: "4px 12px" }}
                >
                  {getStatusText(asset.status)}
                </Tag>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/inventory?edit=${asset.id}`)}
                >
                  Edit
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
        <Col xs={24} lg={16}>
          <Card title="Asset Information">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Serial Number" span={2}>
                <Text
                  strong
                  style={{ fontFamily: "monospace", fontSize: "16px" }}
                >
                  {asset.serialNumber}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Item Name">
                {asset.item?.itemName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Item Type">
                {asset.item?.itemType || "-"}
              </Descriptions.Item>
              {asset.brand && (
                <Descriptions.Item label="Brand">
                  {asset.brand}
                </Descriptions.Item>
              )}
              {asset.batchId && (
                <Descriptions.Item label="Batch ID">
                  {asset.batchId}
                </Descriptions.Item>
              )}
              {asset.purchaseDate && (
                <Descriptions.Item label="Purchase Date">
                  {new Date(asset.purchaseDate).toLocaleDateString()}
                </Descriptions.Item>
              )}
              {asset.warrantyExpiry && (
                <Descriptions.Item label="Warranty Expiry">
                  {new Date(asset.warrantyExpiry).toLocaleDateString()}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(asset.status)}>
                  {getStatusText(asset.status)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Lifecycle History" style={{ marginTop: "16px" }}>
            <Timeline items={timelineItems} />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          {asset.vendor && (
            <Card
              title={
                <Space>
                  <ShopOutlined />
                  <span>Vendor Information</span>
                </Space>
              }
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Vendor Name">
                  {asset.vendor.vendorName}
                </Descriptions.Item>
                {asset.vendor.gstn && (
                  <Descriptions.Item label="GSTN">
                    {asset.vendor.gstn}
                  </Descriptions.Item>
                )}
                {asset.vendor.vendorMobile && (
                  <Descriptions.Item label="Mobile">
                    {asset.vendor.vendorMobile}
                  </Descriptions.Item>
                )}
                {asset.vendor.vendorEmail && (
                  <Descriptions.Item label="Email">
                    {asset.vendor.vendorEmail}
                  </Descriptions.Item>
                )}
                {asset.vendor.vendorAddress && (
                  <Descriptions.Item label="Address">
                    {asset.vendor.vendorAddress}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Divider />
              <Button
                type="link"
                block
                onClick={() => navigate(`/vendors/${asset.vendor.id}`)}
              >
                View Vendor Details
              </Button>
            </Card>
          )}

          {asset.customer && (
            <Card
              title={
                <Space>
                  <UserOutlined />
                  <span>Customer Information</span>
                </Space>
              }
              style={{ marginTop: "16px" }}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Customer Name">
                  {asset.customer.customerName}
                </Descriptions.Item>
                {asset.customer.customerMobile && (
                  <Descriptions.Item label="Mobile">
                    {asset.customer.customerMobile}
                  </Descriptions.Item>
                )}
                {asset.customer.customerEmail && (
                  <Descriptions.Item label="Email">
                    {asset.customer.customerEmail}
                  </Descriptions.Item>
                )}
                {asset.customer.customerAddress && (
                  <Descriptions.Item label="Address">
                    {asset.customer.customerAddress}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Divider />
              <Button
                type="link"
                block
                onClick={() => navigate(`/customers/${asset.customer.id}`)}
              >
                View Customer Details
              </Button>
            </Card>
          )}

          {asset.order && (
            <Card
              title={
                <Space>
                  <ShoppingCartOutlined />
                  <span>Order Information</span>
                </Space>
              }
              style={{ marginTop: "16px" }}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Order Reference">
                  {asset.order.contractReference || `Order #${asset.order.id}`}
                </Descriptions.Item>
                <Descriptions.Item label="Order Date">
                  {new Date(asset.order.orderDate).toLocaleDateString()}
                </Descriptions.Item>
                <Descriptions.Item label="Order Value">
                  ₹{parseFloat(asset.order.orderValue || 0).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Project Status">
                  <Tag>{asset.order.projectStatus}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Payment Status">
                  <Tag>{asset.order.paymentStatus}</Tag>
                </Descriptions.Item>
              </Descriptions>
              <Divider />
              <Button
                type="link"
                block
                onClick={() => navigate(`/orders/${asset.order.id}`)}
              >
                View Order Details
              </Button>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AssetDetail;
